import { useEffect, useRef } from "react";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FaSpinner, FaCheck, FaXmark } from "react-icons/fa6";
import { css } from "../../../../styled-system/css";
import { Flex, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { useAmazonAdvertisingOAuthExchange } from "../../../modules/onboarding/amazon/useAmazonAdvertisingOAuthExchange";
import { decodeOAuthState } from "../../../modules/onboarding/amazon/initAmazonAdvertisingOAuth";
import { AMAZON_ONBOARDING_STATUS_QUERY_KEY } from "../../../modules/onboarding/amazon/useAmazonOnboardingStatus";
import { getSameOriginPath } from "../../../utils/redirect";

const DEFAULT_SUCCESS_PATH = "/onboarding/v2/profiles";
const DEFAULT_ERROR_PATH = "/";

function AmazonAdvertisingOAuthCallback() {
  const { t } = useTranslation(["onboarding"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const exchangeMutation = useAmazonAdvertisingOAuthExchange();
  const hasInitiated = useRef(false);

  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const stateParam = params.get("state");

  const decodedState = decodeOAuthState(stateParam);
  // `state` comes back in the callback URL, so anyone can forge it: only follow same-origin paths.
  const successPath = getSameOriginPath(decodedState?.successPath, DEFAULT_SUCCESS_PATH);
  const errorPath = getSameOriginPath(decodedState?.errorPath, DEFAULT_ERROR_PATH);
  const reauthorize = decodedState?.reauthorize;
  const additional_ads_credentials = decodedState?.additional_ads_credentials;

  const hasCode = Boolean(code);
  const validationError = !hasCode ? t(($) => $.onboarding["Invalid callback URL: missing authorization code"]) : null;

  useEffect(() => {
    if (!code || hasInitiated.current || exchangeMutation.isPending || exchangeMutation.isSuccess) {
      return;
    }

    hasInitiated.current = true;

    exchangeMutation.mutate(
      {
        amazon_authorization_code: code,
        reauthorize,
        additional_ads_credentials,
      },
      {
        onSuccess: () => {
          // Invalidate caches so other components fetch fresh data
          void queryClient.invalidateQueries({ queryKey: AMAZON_ONBOARDING_STATUS_QUERY_KEY });
          void queryClient.invalidateQueries({ queryKey: ["amazonProfiles"] });

          void navigate({ href: successPath });
        },
      },
    );
  }, [code, exchangeMutation, reauthorize, additional_ads_credentials, queryClient, navigate, successPath]);

  const handleRetry = () => {
    hasInitiated.current = false;
    exchangeMutation.reset();
  };

  const handleGoBack = () => {
    void navigate({ href: errorPath });
  };

  const errorMessage =
    validationError ||
    (exchangeMutation.error instanceof Error ? exchangeMutation.error.message : null) ||
    (exchangeMutation.isError ? t(($) => $.onboarding["Failed to connect Amazon Advertising account"]) : "");

  const hasError = validationError || exchangeMutation.isError;

  return (
    <Flex
      role="status"
      aria-live="polite"
      aria-atomic="true"
      gap="small"
      direction="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      padding="large"
    >
      {(exchangeMutation.isPending || (exchangeMutation.isIdle && !validationError)) && (
        <>
          <div
            className={css({
              color: "primary",
              fontSize: "xxlarge",
              animation: "spin 1s linear infinite",
            })}
            role="img"
            aria-label={t(($) => $.onboarding["Loading icon"])}
          >
            <FaSpinner />
          </div>
          <div className={css({ fontSize: "large", fontWeight: "bold" })}>
            {t(($) => $.onboarding["Connecting Amazon Advertising..."])}
          </div>
          <div className={css({ color: "gray.600", textAlign: "center" })}>
            {t(($) => $.onboarding["Please wait while we connect your Amazon Advertising account."])}
          </div>
        </>
      )}

      {exchangeMutation.isSuccess && (
        <>
          <div
            className={css({ color: "success", fontSize: "xxlarge" })}
            role="img"
            aria-label={t(($) => $.onboarding["Success icon"])}
          >
            <FaCheck />
          </div>
          <div className={css({ fontSize: "large", fontWeight: "bold" })}>
            {t(($) => $.onboarding["Successfully Connected!"])}
          </div>
          <div className={css({ color: "gray.600", textAlign: "center" })}>
            {t(($) => $.onboarding["Redirecting..."])}
          </div>
        </>
      )}

      {hasError && (
        <VStack gap="medium" alignItems="center" maxWidth="500px">
          <div
            className={css({ color: "error", fontSize: "xxlarge" })}
            role="img"
            aria-label={t(($) => $.onboarding["Error icon"])}
          >
            <FaXmark />
          </div>
          <div className={css({ fontSize: "large", fontWeight: "bold" })}>
            {t(($) => $.onboarding["Connection Failed"])}
          </div>
          <div className={css({ color: "gray.700", textAlign: "center" })} role="alert">
            {errorMessage}
          </div>
          <Flex gap="medium" marginTop="medium">
            <Button variant="secondary" onPress={handleGoBack}>
              {t(($) => $.onboarding["Go Back"])}
            </Button>
            {!validationError && (
              <Button onPress={handleRetry} colorScheme="fuchsia">
                {t(($) => $.onboarding["Try Again"])}
              </Button>
            )}
          </Flex>
        </VStack>
      )}
    </Flex>
  );
}

export const Route = createFileRoute("/oauth/amazon/advertising")({
  component: AmazonAdvertisingOAuthCallback,
});
